import { Flex, Icon, Text } from '@chakra-ui/react';
import { FileCheck2Icon, FileWarningIcon } from 'lucide-react';
import { FC } from 'react';
import { ComplianceDocumentSummary } from './types';

interface DocumentTabsProps {
  /** The documents under the active checklist item. */
  docs: ComplianceDocumentSummary[];
  selectedDocumentId?: string;
  /**
   * Live per-document count of items still needing attention (non-ignored
   * suggestions + open checklist issues), keyed by ingestion document id. Drives
   * the warning icon + count badge so the agent's in-session ignores update them
   * immediately. Falls back to the server `openSuggestionCount` when absent.
   */
  suggestionCounts: Record<string, number>;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * Center tab strip: one tab per ingestion document under the active checklist
 * item. The active tab is highlighted; switching tabs swaps the PDF + its
 * suggestions. A single-document item still renders its one tab. Scrolls
 * horizontally when the tabs overflow.
 */
export const DocumentTabs: FC<DocumentTabsProps> = ({
  docs,
  selectedDocumentId,
  suggestionCounts,
  onSelectDocument,
}) => {
  if (docs.length === 0) {
    return null;
  }

  return (
    <Flex
      align="stretch"
      borderBottomColor="compliance.border.primary"
      borderBottomWidth={1}
      flexShrink={0}
      overflowX="auto"
      role="tablist"
      w="full"
    >
      {docs.map((doc) => {
        const isSelected = doc.ingestionDocumentId === selectedDocumentId;
        // A tab with open (non-ignored) items gets the yellow warning icon + a
        // count, so the agent can see which documents still need attention
        // without opening each tab. Uses the live count (falls back to the
        // server count until the per-tab detail loads).
        const suggestionCount =
          suggestionCounts[doc.ingestionDocumentId] ?? doc.openSuggestionCount;
        const hasSuggestions = suggestionCount > 0;

        return (
          <Flex
            key={doc.ingestionDocumentId}
            _hover={{
              bg: isSelected
                ? 'compliance.group.expanded.bg'
                : 'compliance.hover.bg',
            }}
            align="center"
            aria-selected={isSelected}
            bg={isSelected ? 'compliance.group.expanded.bg' : 'transparent'}
            borderRightColor="compliance.border.primary"
            borderRightWidth={1}
            cursor="pointer"
            flexShrink={0}
            gap={1.5}
            maxW="280px"
            px={5}
            py={4}
            role="tab"
            tabIndex={0}
            onClick={() => onSelectDocument(doc.ingestionDocumentId)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectDocument(doc.ingestionDocumentId);
              }
            }}
          >
            <Icon
              as={hasSuggestions ? FileWarningIcon : FileCheck2Icon}
              boxSize="14px"
              color={
                hasSuggestions
                  ? 'compliance.status.warning'
                  : isSelected
                    ? 'compliance.text.primary'
                    : 'compliance.text.muted'
              }
              flexShrink={0}
            />
            <Text
              color={
                isSelected ? 'compliance.text.primary' : 'compliance.text.muted'
              }
              fontSize="sm"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {doc.documentName}
            </Text>
            {hasSuggestions && (
              <Flex
                align="center"
                bg="whiteAlpha.100"
                borderRadius="6px"
                flexShrink={0}
                justify="center"
                px={1.5}
              >
                <Text color="compliance.status.warning" fontSize="xs">
                  {suggestionCount}
                </Text>
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
